/**
 * Schedules local notifications for grant and session expiry.
 *
 * Called after each status poll with the current session/ACL data.
 * Cancels all previous expiry notifications and reschedules based on
 * current timestamps so the schedule stays in sync with server-side
 * changes (renewals, extensions, revocations).
 *
 * Uses Capacitor LocalNotifications which schedules via Android's
 * AlarmManager, so notifications fire even when the app is backgrounded.
 *
 * Notification IDs:
 *   1000  = session expiry warning (5 min before)
 *   1001  = session expired
 *   2000+ = grant expiry warnings (one per ACL, indexed)
 */

import { isAndroid, isNativePlatform } from "./platform";

const FIVE_MINUTES_MS = 5 * 60 * 1000;

// Keep in sync with IpChangePlugin.kt delegated object constants
const SESSION_WARNING_ID = 1000;
const SESSION_EXPIRED_ID = 1001;
const GRANT_WARNING_BASE_ID = 2000;
const EXPIRY_CHANNEL_ID = "knocknoc_expiry";

let _initialized = false;
let _isInForeground = true;
let _lastSession: SessionData | null = null;
let _lastAclCount = 0;

export async function initExpiryNotifications(): Promise<void> {
  if (!isNativePlatform() || _initialized) return;
  _initialized = true;

  const { LocalNotifications } = await import("@capacitor/local-notifications");

  // Permission is requested via NotificationPermissionOverlay, which gates
  // the whole app until the user grants it. Don't request silently here.

  // createChannel is Android-only; iOS routes notifications through user
  // settings without per-channel configuration.
  if (isAndroid()) {
    try {
      await LocalNotifications.createChannel({
        id: EXPIRY_CHANNEL_ID,
        name: "Access Expiry Alerts",
        description: "Notifications when your session or access is about to expire",
        importance: 4,
      });
    } catch {
      // Best-effort: a missing channel just means default importance/sound.
    }
  }

  // Cancel any leftover scheduled notifications from a previous session
  // (e.g. after the native logout path reloads the WebView).
  await cancelExpiryNotifications();

  const { App } = await import("@capacitor/app");
  await App.addListener("appStateChange", (state) => {
    _isInForeground = state.isActive;
    if (state.isActive) {
      cancelExpiryNotifications();
    } else if (_lastSession) {
      doScheduleNotifications(_lastSession);
    }
  });
}

interface ACLData {
  Description?: string;
  ExpiredAfter?: string;
}

interface SessionData {
  Ends?: string;
  ACLs?: ACLData[];
}

export async function scheduleExpiryNotifications(session: SessionData): Promise<void> {
  if (!isNativePlatform()) return;

  _lastSession = session;

  // Only schedule notifications when the app is backgrounded.
  // When foregrounded, the user can see expiry info on screen.
  if (_isInForeground) return;

  await doScheduleNotifications(session);
}

async function doScheduleNotifications(session: SessionData): Promise<void> {
  const { LocalNotifications } = await import("@capacitor/local-notifications");

  const idsToCancel = [SESSION_WARNING_ID, SESSION_EXPIRED_ID];
  const acls = session.ACLs ?? [];
  const cancelCount = Math.max(acls.length, _lastAclCount);
  for (let i = 0; i < cancelCount; i++) {
    idsToCancel.push(GRANT_WARNING_BASE_ID + i);
  }
  _lastAclCount = acls.length;

  await LocalNotifications.cancel({ notifications: idsToCancel.map((id) => ({ id })) });

  const now = Date.now();
  const notifications: Array<{
    id: number;
    title: string;
    body: string;
    channelId: string;
    schedule: { at: Date; allowWhileIdle: boolean };
  }> = [];

  if (session.Ends) {
    const endsAt = new Date(session.Ends).getTime();
    if (!isNaN(endsAt)) {
      const warningAt = endsAt - FIVE_MINUTES_MS;

      if (warningAt > now) {
        notifications.push({
          id: SESSION_WARNING_ID,
          title: "Session Expiring",
          body: "Your Knocknoc session expires in 5 minutes.",
          channelId: EXPIRY_CHANNEL_ID,
          schedule: { at: new Date(warningAt), allowWhileIdle: true },
        });
      }

      if (endsAt > now) {
        notifications.push({
          id: SESSION_EXPIRED_ID,
          title: "Session Expired",
          body: "Your Knocknoc session has expired.",
          channelId: EXPIRY_CHANNEL_ID,
          schedule: { at: new Date(endsAt), allowWhileIdle: true },
        });
      }
    }
  }

  for (let i = 0; i < acls.length; i++) {
    const acl = acls[i];
    if (!acl.ExpiredAfter) continue;

    const expiresAt = new Date(acl.ExpiredAfter).getTime();
    if (isNaN(expiresAt)) continue;
    const warningAt = expiresAt - FIVE_MINUTES_MS;

    if (warningAt > now) {
      const name = acl.Description || "an ACL";
      notifications.push({
        id: GRANT_WARNING_BASE_ID + i,
        title: "Access Expiring",
        body: `Access to ${name} expires in 5 minutes.`,
        channelId: EXPIRY_CHANNEL_ID,
        schedule: { at: new Date(warningAt), allowWhileIdle: true },
      });
    }
  }

  if (notifications.length > 0) {
    await LocalNotifications.schedule({ notifications });
  }
}

export async function cancelExpiryNotifications(): Promise<void> {
  if (!isNativePlatform()) return;

  // Clear cached session so the appStateChange listener doesn't
  // reschedule notifications from stale data after logout.
  _lastSession = null;

  const { LocalNotifications } = await import("@capacitor/local-notifications");
  const ids = [SESSION_WARNING_ID, SESSION_EXPIRED_ID];
  const cancelCount = Math.max(_lastAclCount, 50);
  for (let i = 0; i < cancelCount; i++) {
    ids.push(GRANT_WARNING_BASE_ID + i);
  }
  await LocalNotifications.cancel({ notifications: ids.map((id) => ({ id })) });
}
